import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, LogOut } from "lucide-react";
import { api } from "@/lib/api";

function nowParts() {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { day: d.getDay(), hhmm: `${hh}:${mm}`, date };
}

function parseTime(t) {
  const m = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(t || "");
  if (!m) return { h: 11, min: 0 };
  let h = Number(m[1]);
  const min = Number(m[2]);
  const ap = (m[3] || "").toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return { h, min };
}

export default function ReminderOverlay() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [reminders, setReminders] = useState([]);
  const [info, setInfo] = useState(null);
  const [current, setCurrent] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const audioCtxRef = useRef(null);
  const soundTimerRef = useRef(null);

  const ensureCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtxRef.current = new AC();
    }
    return audioCtxRef.current;
  }, []);

  const playChime = useCallback(() => {
    const ctx = ensureCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const t = ctx.currentTime;
    [880, 1174.66, 880].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      const t0 = t + i * 0.22;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.55, t0 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.42);
    });
  }, [ensureCtx]);

  // Unlock audio on the first user interaction anywhere in the app (browsers block autoplay).
  useEffect(() => {
    const unlock = () => {
      const ctx = ensureCtx();
      if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [ensureCtx]);

  // While a reminder is on screen, chime immediately then every 30s until it's dismissed.
  useEffect(() => {
    if (current && soundEnabled) {
      playChime();
      soundTimerRef.current = setInterval(playChime, 30000);
    }
    return () => {
      if (soundTimerRef.current) {
        clearInterval(soundTimerRef.current);
        soundTimerRef.current = null;
      }
    };
  }, [current, soundEnabled, playChime]);

  const load = useCallback(() => {
    api.get("/reminders").then((r) => setReminders(r.data)).catch(() => {});
    api.get("/checkout-info").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSoundEnabled(r.data?.reminder_sound_enabled !== false)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const check = useCallback(() => {
    // Test/preview mode: open ?preview=checkout to force the check-out reminder to appear.
    if (params.get("preview") === "checkout") {
      const co = reminders.find((r) => r.trigger === "checkout");
      setCurrent({
        id: co?.id || "preview",
        title: co?.title || "Time to check out",
        message: co?.message || "",
        _checkout: true,
        _instructions: info?.checkout_instructions,
        _key: "preview",
      });
      return;
    }

    const { day, hhmm, date } = nowParts();
    const now = new Date();

    // 1) Scheduled reminders (day + time window)
    let active = reminders.find(
      (r) => (r.trigger || "schedule") === "schedule" && r.enabled && (r.days || []).includes(day) && hhmm >= r.start_time && hhmm <= r.end_time
    );

    // 2) Check-out reminders: show from 3:00 AM on the check-out day until the check-out time.
    if (!active && info && info.checkout_date) {
      const { h, min } = parseTime(info.checkout_time);
      const [Y, Mo, D] = info.checkout_date.split("-").map(Number);
      const coDate = new Date(Y, Mo - 1, D, h, min, 0, 0);
      const start = new Date(Y, Mo - 1, D, 3, 0, 0, 0); // 3:00 AM on the check-out day
      const co = reminders.find((r) => {
        if (r.trigger !== "checkout" || !r.enabled) return false;
        return now >= start && now <= coDate;
      });
      if (co) active = { ...co, _checkout: true, _instructions: info.checkout_instructions, _key: info.checkout_date };
    }

    if (!active) { setCurrent(null); return; }
    const keyId = active._checkout ? `${active.id}_${active._key}` : `${active.id}_${date}_${active.start_time}`;
    if (localStorage.getItem(`reminder_dismissed_${keyId}`)) { setCurrent(null); return; }
    setCurrent(active);
  }, [reminders, info, params]);

  useEffect(() => {
    check();
    const t = setInterval(check, 30 * 1000);
    return () => clearInterval(t);
  }, [check]);

  const dismiss = () => {
    if (current) {
      const { date } = nowParts();
      const keyId = current._checkout ? `${current.id}_${current._key}` : `${current.id}_${date}_${current.start_time}`;
      localStorage.setItem(`reminder_dismissed_${keyId}`, "1");
    }
    setCurrent(null);
  };

  const handleTap = () => {
    const goToCheckout = !!current?._checkout;
    dismiss();
    if (goToCheckout) navigate("/house-info?instr=checkout");
  };

  const isCheckout = !!current?._checkout;
  const Icon = isCheckout ? LogOut : Trash2;
  const body = current?.message;

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          data-testid="reminder-overlay"
          onClick={handleTap}
          onTouchStart={handleTap}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] bg-brand-terracotta text-white flex flex-col items-center justify-center p-6 sm:p-10 text-center cursor-pointer select-none overflow-y-auto"
        >
          {isCheckout ? (
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              className="w-full max-w-md flex flex-col items-center"
            >
              <div className="mx-auto mb-6 sm:mb-8 h-20 w-20 sm:h-28 sm:w-28 rounded-full bg-white/15 flex items-center justify-center animate-bounce">
                <LogOut className="h-10 w-10 sm:h-14 sm:w-14" />
              </div>
              <span className="overline text-white/80">Check-out Reminder</span>
              <h1
                data-testid="reminder-title"
                className="font-display text-3xl sm:text-5xl leading-tight tracking-tight mt-3 mb-8 sm:mb-10 px-2 break-words"
              >
                Click here for check&#8209;out instructions
              </h1>
              <span
                data-testid="reminder-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white text-brand-terracotta px-7 sm:px-8 py-3.5 sm:py-4 text-base sm:text-lg font-semibold shadow-lg animate-pulse"
              >
                View Check-out Instructions
              </span>
            </motion.div>
          ) : (
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              className="w-full max-w-2xl"
            >
              <div className="mx-auto mb-6 sm:mb-8 h-20 w-20 sm:h-28 sm:w-28 rounded-full bg-white/15 flex items-center justify-center animate-bounce">
                <Icon className="h-10 w-10 sm:h-14 sm:w-14" />
              </div>
              <span className="overline text-white/80">Reminder</span>
              <h1 data-testid="reminder-title" className="font-display text-4xl sm:text-6xl tracking-tight mt-3 mb-5 break-words">{current.title}</h1>
              <p data-testid="reminder-body" className="text-lg sm:text-2xl text-white/90 leading-relaxed mb-8 sm:mb-10 whitespace-pre-line text-left sm:text-center">{body}</p>
              <span className="inline-flex items-center gap-2 rounded-full bg-white text-brand-terracotta px-7 sm:px-8 py-3.5 sm:py-4 text-base sm:text-lg font-semibold shadow-lg animate-pulse">
                Tap anywhere to dismiss
              </span>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
